import { Ball, SimulationConfig, RenderLayer, MorphData, TrailCircle, CollisionEvent, BlastEvent, EscapeRing, ShapeType, BallPiece, RhythmicSimulationState } from '../types';
import { CAMERA } from '../services/CameraService';
import { drawBackground, drawContainer, drawBall, drawOutsideCollider } from './drawers/SimulationDrawers';
import { drawBlastEvent, drawCollisionEffect } from '../services/BreakAnimations';
import { WebGLRenderer } from './WebGLRenderer';
import { EscapeLogic } from '../logic/modes/EscapeLogic';
import { SpaceSpiralLogic } from '../logic/modes/SpaceSpiralLogic';
import { BasicModesLogic } from '../logic/modes/BasicModesLogic';
import { BallBreakAnimation } from '../logic/animations/BallBreakAnimation';
import { ShapeUtils } from '../logic/physics/ShapeUtils';
import { BackgroundRenderer } from './BackgroundRenderer';
import { ParallaxRenderer } from './ParallaxRenderer';
import { SpectrumRenderer } from './SpectrumRenderer';

import { HUDRenderer } from './HUDRenderer';

export const SimulationRenderer = {
  draw: (
    canvas: HTMLCanvasElement,
    bgCanvas: HTMLCanvasElement | null,
    webglRenderer: WebGLRenderer | null,
    balls: Ball[],
    trailCircles: TrailCircle[],
    collisionEvents: CollisionEvent[],
    blastEvents: BlastEvent[],
    persistentSpectrumBoost: Float32Array,
    escapeRings: EscapeRing[],
    initialEscapeRingCount: number,
    config: SimulationConfig,
    hue: number,
    shake: number,
    counterPop: number,
    timeRemaining: number,
    morphState: { active: boolean, from: ShapeType, to: ShapeType, progress: number },
    containerRadius: number,
    rhythmicBreakPieces: BallPiece[],
    zoomLevel: number = 1.0,
    bounceCount: number = 0,
    previews?: { pos: {x:number, y:number}, vel: {x:number, y:number}, radius: number, color: string }[],
    rhythmicSimulationsState?: RhythmicSimulationState[],
    rhythmicEscapeShieldActive: boolean = false,
    rhythmicEscapeShieldAngle: number = 0
  ) => {
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    const bgCtx = bgCanvas?.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    const now = Date.now();
    const centerX = (canvas.width / dpr) / 2, centerY = (canvas.height / dpr) / 2, globalHue = hue;
    const cam = CAMERA.update(canvas.width / dpr, canvas.height / dpr, balls, config, shake);
    
    if (webglRenderer) webglRenderer.clear();
    
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    
    if (bgCtx) {
        bgCtx.setTransform(1, 0, 0, 1, 0, 0);
        bgCtx.scale(dpr, dpr);
        BackgroundRenderer.draw(bgCtx, config, canvas.width / dpr, canvas.height / dpr, globalHue);
        ParallaxRenderer.draw(bgCtx, config, cam, canvas.width / dpr, canvas.height / dpr, globalHue);
    } else {
        BackgroundRenderer.draw(ctx, config, canvas.width / dpr, canvas.height / dpr, globalHue);
        ParallaxRenderer.draw(ctx, config, cam, canvas.width / dpr, canvas.height / dpr, globalHue);
    }
    
    ctx.save(); 
    
    // Total scale combining zoomLevel and camera scale
    const totalScale = zoomLevel * cam.scale;
    
    // Apply transform centered on screen
    ctx.translate(centerX, centerY);
    ctx.scale(totalScale, totalScale);
    // cam.x and cam.y are already relative to (0,0) in physics
    ctx.translate(cam.x, cam.y);

    drawOutsideCollider(ctx, config);

    const morphData: MorphData | undefined = morphState.active ? {
        from: morphState.from,
        to: morphState.to,
        factor: morphState.progress,
        baseRadius: config.containerRadius
    } : {
        from: config.containerShape,
        to: config.containerShape,
        factor: 0,
        baseRadius: config.containerRadius
    };

    const rgbStr = `hsl(${globalHue}, 100%, 50%)`;

    const renderLayers: Record<RenderLayer, () => void> = {
        WALLS: () => {
            if (config.mode === 'SPACE_SPIRAL') return; 
            if ((config.mode === 'ESCAPE' || config.mode === 'RHYTHMIC_ESCAPE') && !config.escapeShowOuterBorders) return;
            
            if (config.mode === 'RHYTHMIC' && config.rhythmicMultiSimEnabled && rhythmicSimulationsState) {
                rhythmicSimulationsState.forEach(sim => {
                    if (!sim.active) return;
                    
                    // Spawn animation
                    const spawnElapsed = now - sim.spawnTime;
                    const spawnDuration = 1000;
                    const spawnProgress = Math.min(1, spawnElapsed / spawnDuration);
                    const spawnScale = 0.5 + 0.5 * Math.sin(spawnProgress * Math.PI / 2);
                    const spawnAlpha = spawnProgress;

                    ctx.save();
                    ctx.translate(sim.center.x, sim.center.y);
                    ctx.scale(spawnScale, spawnScale);
                    ctx.globalAlpha *= spawnAlpha;
                    
                    const mergedCfg = { ...config, ...sim.settings };
                    const simMorphData = { ...morphData, baseRadius: sim.containerRadius };
                    drawContainer(ctx, 0, 0, mergedCfg, globalHue, sim.containerRadius, simMorphData);
                    
                    ctx.restore();
                });
            } else {
                if (webglRenderer) {
                    const morphData = morphState.active ? {
                        from: morphState.from,
                        to: morphState.to,
                        factor: morphState.progress
                    } : {
                        from: config.containerShape,
                        to: config.containerShape,
                        factor: 0
                    };
                    webglRenderer.renderContainer(config, cam, morphData, containerRadius, globalHue, zoomLevel);
                } else {
                    drawContainer(ctx, 0, 0, config, globalHue, containerRadius, morphData);
                }
            }
        },
        RINGS: () => {
            if (config.mode === 'ESCAPE' || config.mode === 'RHYTHMIC_ESCAPE') {
                if (config.mode === 'RHYTHMIC_ESCAPE' && rhythmicEscapeShieldActive) {
                    const radius = config.rhythmicEscapeShieldRingRadius;
                    const lw = config.escapeRingThickness || 4;
                    
                    ctx.save();
                    ctx.beginPath();
                    ctx.strokeStyle = `hsla(${globalHue}, 100%, 70%, 1.0)`;
                    ctx.lineWidth = lw;
                    
                    if (morphData.factor > 0 || morphData.from !== 'CIRCLE') {
                        // Drawing complex shape shield with rotation
                        for (let a = 0; a <= Math.PI * 2 + 0.1; a += 0.1) {
                            const r = ShapeUtils.getInterpolatedRadiusAtAngle(a - rhythmicEscapeShieldAngle, morphData.from, morphData.to, morphData.factor, radius, morphData.baseRadius);
                            const px = Math.cos(a) * r;
                            const py = Math.sin(a) * r;
                            if (a === 0) ctx.moveTo(px, py);
                            else ctx.lineTo(px, py);
                        }
                    } else {
                        ctx.arc(0, 0, radius, 0, Math.PI * 2);
                    }
                    ctx.stroke();
                    ctx.restore();
                }
                EscapeLogic.render(ctx, 0, 0, escapeRings, config, globalHue, morphData, webglRenderer, cam, initialEscapeRingCount, timeRemaining, balls, counterPop, zoomLevel);
            }
        },
        TRAIL: () => {
            const useWebGL = webglRenderer && 
                             (config.ballShape === 'CIRCLE' || !config.ballShape) && 
                             (config.ballTexture === 'NONE' || !config.ballTexture);
            BasicModesLogic.renderTrail(ctx, trailCircles, config, useWebGL ? webglRenderer : null, cam, globalHue, balls, zoomLevel);
        },
        BALL: () => {
            if (previews && previews.length > 0) {
                if (webglRenderer) {
                    webglRenderer.renderBallPreviews(previews, config, cam, zoomLevel);
                } else {
                    previews.forEach(p => {
                        // Draw ball preview
                        ctx.save();
                        ctx.beginPath();
                        ctx.globalAlpha = 0.5;
                        ctx.fillStyle = p.color;
                        ctx.arc(p.pos.x, p.pos.y, p.radius, 0, Math.PI * 2);
                        ctx.fill();
                        
                        // Draw direction arrow
                        const speed = Math.sqrt(p.vel.x * p.vel.x + p.vel.y * p.vel.y);
                        if (speed > 0.1) {
                            const arrowLen = 30;
                            const angle = Math.atan2(p.vel.y, p.vel.x);
                            const endX = p.pos.x + Math.cos(angle) * arrowLen;
                            const endY = p.pos.y + Math.sin(angle) * arrowLen;
                            
                            ctx.beginPath();
                            ctx.strokeStyle = p.color;
                            ctx.lineWidth = 2;
                            ctx.moveTo(p.pos.x, p.pos.y);
                            ctx.lineTo(endX, endY);
                            
                            // Arrow head
                            const headLen = 8;
                            ctx.lineTo(endX - headLen * Math.cos(angle - Math.PI / 6), endY - headLen * Math.sin(angle - Math.PI / 6));
                            ctx.moveTo(endX, endY);
                            ctx.lineTo(endX - headLen * Math.cos(angle + Math.PI / 6), endY - headLen * Math.sin(angle + Math.PI / 6));
                            ctx.stroke();
                        }
                        ctx.restore();
                    });
                }
            }
            
            if (config.mode === 'SPACE_SPIRAL') {
                SpaceSpiralLogic.render(ctx, balls, config, rgbStr, drawBall);
            } else if (config.mode === 'RHYTHMIC' && config.rhythmicMultiSimEnabled && rhythmicSimulationsState) {
                balls.forEach(b => {
                    if (b.isBreaking) return;
                    
                    const sim = rhythmicSimulationsState.find(s => s.ball?.id === b.id);
                    if (sim) {
                        if (!sim.active) return; // HIDDEN when inactive

                        const spawnElapsed = now - sim.spawnTime;
                        const spawnDuration = 1000;
                        const spawnProgress = Math.min(1, spawnElapsed / spawnDuration);
                        const spawnScale = 0.5 + 0.5 * Math.sin(spawnProgress * Math.PI / 2);
                        const spawnAlpha = spawnProgress;

                        ctx.save();
                        ctx.translate(sim.center.x, sim.center.y);
                        ctx.scale(spawnScale, spawnScale);
                        ctx.translate(-sim.center.x, -sim.center.y);
                        ctx.globalAlpha *= spawnAlpha;
                        const mergedCfg = { ...config, ...sim.settings };
                        drawBall(ctx, b, mergedCfg, b.color);
                        ctx.restore();
                    } else {
                        drawBall(ctx, b, config, b.color);
                    }
                });
            } else {
                const useWebGL = webglRenderer && 
                                 (config.ballShape === 'CIRCLE' || !config.ballShape) && 
                                 (config.ballTexture === 'NONE' || !config.ballTexture);

                if (useWebGL && webglRenderer) {
                    webglRenderer.render(balls, config, cam, zoomLevel);
                } else {
                    balls.forEach(b => {
                        if (b.isBreaking) return;
                        drawBall(ctx, b, config, b.color);
                    });
                }
            }

            // Draw Rhythmic Break Pieces
            if (rhythmicBreakPieces.length > 0) {
                BallBreakAnimation.drawPieces(ctx, rhythmicBreakPieces, now);
            }
        },
        FX: () => {
            if (config.mode === 'SPACE_SPIRAL') return;
            if (webglRenderer) {
                webglRenderer.renderFX(collisionEvents, config, cam, zoomLevel);
            } else {
                collisionEvents.forEach(e => {
                    drawCollisionEffect(ctx, e, config, globalHue, canvas.width / dpr, canvas.height / dpr, cam);
                });
            }
        },
        BLAST: () => {
            // Force Canvas for blasts to support complex animations like SHATTER
            blastEvents.forEach(e => drawBlastEvent(ctx, e, config, globalHue, canvas.width / dpr, canvas.height / dpr, cam)); 
        },
        SPECTRUM: () => {
            SpectrumRenderer.render(ctx, persistentSpectrumBoost, config, globalHue, morphData);
        },
        HUD: () => {
            HUDRenderer.render(
                ctx,
                0,
                0,
                canvas,
                dpr,
                balls,
                escapeRings,
                initialEscapeRingCount,
                config,
                globalHue,
                counterPop,
                timeRemaining,
                config.mode === 'RHYTHMIC_ESCAPE' ? (config.rhythmicEscapeShieldRingRadius || 100) : containerRadius,
                bounceCount
            );
        }
    };

    const defaultOrder: RenderLayer[] = ['WALLS', 'SPECTRUM', 'RINGS', 'TRAIL', 'BLAST', 'FX', 'BALL', 'HUD'];
    const order = config.renderOrder || defaultOrder;
    
    // Ensure SPECTRUM is in the order if it's missing (for existing configs)
    const finalOrder = (order.includes('SPECTRUM') ? order : ['WALLS', 'SPECTRUM', ...order.filter(l => l !== 'WALLS' && l !== 'SPECTRUM')]) as RenderLayer[];

    finalOrder.forEach((layer: RenderLayer) => {
        if (renderLayers[layer]) renderLayers[layer]();
    });

    ctx.restore();
  }
};
